import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const RECIPES_URL = 'https://evercorenutrition.com/ev_admin/api/recipes/';

// ─── Async Thunk: Fetch All Recipes ──────────────────────────────────────────
export const fetchRecipes = createAsyncThunk(
  'recipes/fetchRecipes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(RECIPES_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err?.message || 'Failed to fetch recipes');
      }
      const data = await response.json();
      // API returns { count, next, previous, results: [...] }
      return {
        count:    data.count    || 0,
        next:     data.next     || null,
        previous: data.previous || null,
        results:  data.results  || [],
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Async Thunk: Fetch Single Recipe by ID ───────────────────────────────────
export const fetchRecipeById = createAsyncThunk(
  'recipes/fetchRecipeById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${RECIPES_URL}${id}/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err?.message || 'Failed to fetch recipe');
      }
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message || 'Network error');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────
const recipesSlice = createSlice({
  name: 'recipes',
  initialState: {
    list:           [],    // all recipes from results[]
    selected:       null,  // single recipe detail
    count:          0,
    next:           null,
    previous:       null,
    loading:        false,
    detailLoading:  false,
    error:          null,
  },
  reducers: {
    clearSelectedRecipe(state) {
      state.selected = null;
    },
    clearRecipeError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // ── FETCH ALL ──────────────────────────────────────────────────────────────
    builder
      .addCase(fetchRecipes.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchRecipes.fulfilled, (state, action) => {
        state.loading  = false;
        state.list     = action.payload.results;
        state.count    = action.payload.count;
        state.next     = action.payload.next;
        state.previous = action.payload.previous;
      })
      .addCase(fetchRecipes.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      });

    // ── FETCH SINGLE ───────────────────────────────────────────────────────────
    builder
      .addCase(fetchRecipeById.pending, (state) => {
        state.detailLoading = true;
        state.error         = null;
      })
      .addCase(fetchRecipeById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selected      = action.payload;
      })
      .addCase(fetchRecipeById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error         = action.payload;
      });
  },
});

export const { clearSelectedRecipe, clearRecipeError } = recipesSlice.actions;
export default recipesSlice.reducer;